/**
 * Debounce a call and expose cancellation for unmount cleanup.
 * Replaces the setTimeout/clearTimeout pairs scattered across the search flows.
 */
export function debounce<A extends unknown[]>(
  fn: (...args: A) => void,
  ms: number
): { run: (...args: A) => void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null
  return {
    run(...args: A) {
      if (timer !== null) clearTimeout(timer)
      timer = setTimeout(() => {
        timer = null
        fn(...args)
      }, ms)
    },
    cancel() {
      if (timer !== null) {
        clearTimeout(timer)
        timer = null
      }
    },
  }
}

/**
 * Latest-wins guard for out-of-order async responses. Tag each request with
 * next(); discard any response whose tag isStale() because a newer request
 * has since been issued.
 */
export function latestWins(): {
  next: () => number
  isStale: (tag: number) => boolean
} {
  let current = 0
  return {
    next: () => ++current,
    isStale: (tag: number) => tag !== current,
  }
}
